import { Injectable } from '@angular/core';

import type { TLocation, TRouteLeg, TRoutePoint, TTicket } from '@shared/models';

@Injectable({
    'providedIn': 'root'
})
export class RouteLegsService {
    private routeLegs: TRouteLeg[] = [];

    public buildRouteLegs(startingPoint: TRoutePoint, endingPoint: TRoutePoint, selectedTickets: TTicket[]): TRouteLeg[] {
        this.routeLegs = [];

        this.buildStartingLeg(startingPoint, selectedTickets);
        this.buildMiddleLegs(selectedTickets);
        this.buildEndingLeg(endingPoint, selectedTickets);

        // Assign each leg a waypoint number and a letter.
        this.computeWaypointNumbersAndLetters(startingPoint);
        return this.routeLegs;
    }

    private buildStartingLeg(startingPoint: TRoutePoint, tickets: TTicket[]): void {
        const currLocation = startingPoint.location;
        const nextLocation = this.extractLocation(tickets[0]);
        const nextTicket = tickets[0];

        const leg = this.computeLeg(currLocation, nextLocation, nextTicket);
        this.routeLegs.push(leg);
    }

    private buildMiddleLegs(tickets: TTicket[]): void {
        // Assume we only have 1 dropoff ticket then there won't be any middle legs.
        // The starting leg will go from the dealership to the dropoff point and the ending
        // leg will go from the dropoff point back to the dealership.
        if (tickets.length < 2) {
            return;
        }

        for (let idx = 1; idx < tickets.length; idx++) {
            const prevTicket = tickets[idx - 1];
            const thisTicket = tickets[idx];
            const nextTicket = tickets[idx];

            // It's possible that we have completed a ticket and then a pickup request comes in.
            // We need to insert that ticket without regard to completed tickets.
            if (prevTicket.status === 'completed') continue;
            if (thisTicket.status === 'completed') continue;

            const currLocation = this.extractLocation(prevTicket);
            const nextLocation = this.extractLocation(thisTicket);
            // const passengerName = (thisTicket.fromDealership) ? thisTicket.passengerName : 'No Passenger';

            const leg = this.computeLeg(currLocation, nextLocation, nextTicket);
            this.routeLegs.push(leg);
        }
    }

    private buildEndingLeg(endingPoint: TRoutePoint, tickets: TTicket[]): void {
        const nextTicket = tickets[tickets.length - 1];
        const currLocation = this.extractLocation(nextTicket);
        const nextLocation = endingPoint.location;

        const leg = this.computeLeg(currLocation, nextLocation, nextTicket);
        this.routeLegs.push(leg);
    }

    private computeLeg(currLocation: TLocation, nextLocation: TLocation, nextTicket: TTicket): TRouteLeg {
        const routeLeg: TRouteLeg = {
            'from': {
                'location': currLocation
            },
            'to': {
                'location': nextLocation,
            },
            'status': 'enroute',
            'isUrgent': nextTicket.isUrgent || false,
            'ticketUid': nextTicket.uid,
            'passengerName': (nextTicket.fromDealership) ? nextTicket.passengerName : 'No Passenger'
        };

        return routeLeg;
    }

    // private extractTicketValues(ticket: TTicket) {
    //     const obj = {};

    //     // These attributes are common to both dropoff and pickup.
    //     obj.passengerName = ticket.passengerName;
    //     obj.isUrgent = ticket.isUrgent || false;
    //     obj.fromDealership = ticket.fromDealership;
    //     obj.ticketUid = ticket.uid;

    //     if (ticket.fromDealership) {
    //         obj.locationName = ticket.dropoff.locationName;
    //         obj.address = ticket.dropoff.address;
    //         obj.address1 = ticket.dropoff.address.streetAddress;
    //         obj.address2 = `${ticket.dropoff.address.city}, ${ticket.dropoff.address.zipcode}`;
    //         obj.coordinate = ticket.dropoff.address.coordinate;
    //     } else {
    //         obj.locationName = ticket.pickup.locationName;
    //         obj.address = ticket.pickup.address;
    //         obj.address1 = ticket.pickup.address.streetAddress;
    //         obj.address2 = `${ticket.pickup.address.city}, ${ticket.pickup.address.zipcode}`;
    //         obj.address2.replace(' - ', ', ');
    //         obj.coordinate = ticket.pickup.address.coordinate;
    //     }

    //     return obj;
    // }

    private extractLocation(ticket: TTicket): TLocation {
        const location = (ticket.fromDealership) ? ticket.dropoff : ticket.pickup;
        return location!;
    }

    // Assign each leg a waypoint number and letter.
    // TODO: This method is not working correctly.  The waypoint numbers are not being assigned correctly.
    //      The waypoint numbers are being assigned in the order the tickets are being inserted.
    // The waypoint numbers and letters are not being used for endingPoint.
    // The endingPoint is being hardcoded to have a letter of '?' and a waypoint number of 0.
    // I think endingPoint doesn't need to be passed in.  It should be the last leg in the routeLegs array.
    // private computeWaypointNumbers(startingPoint: TRoutePoint, endingPoint: TRoutePoint): void {
    // Now that I removed the second parameter, endingPoint, it occurs to me I don't need the startingPoint either.
    private computeWaypointNumbersAndLetters(startingPoint: TRoutePoint): void {
        // const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        let idx: number;

        startingPoint.letter = this.computeLetter(0);   // 'A';
        startingPoint.waypointNumber = 1;

        // The starting point was hardcoded to have the letter A.  Begin the middle legs with B.
        // Determine the letters for the middle legs.   // and ending legs.
        for (idx = 0; idx < this.routeLegs.length - 1; idx++) {
            const leg = this.routeLegs[idx];
            leg.letter = this.computeLetter(idx);       // letters[idx % letters.length];
            leg.waypointNumber = idx + 1;
        }

        // Fix up the last leg.
        idx = this.routeLegs.length - 1;
        const lastLeg = this.routeLegs[idx];
        lastLeg.letter = '?';   // Markers are only displayed for letters not the ?
        lastLeg.waypointNumber = idx + 1;
    }

    public computeLetter(idx: number): string {
        const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        const letter = letters[idx % letters.length];

        return letter;
    }
}
